import { startEmailLogin, verifyEmailLogin } from "@/api/auth";
import { updateUserProfileInfo } from "@/api/creator/user-profile";
import GoogleSignIn from "@/components/google-sign-in";
import Modal from "@/components/ui/modal";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const LOGIN_RETURN_TO_KEY = "hotpleLoginReturnTo";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_CODE_COOLDOWN_SECONDS = 60;
const AUTH_NOTICE_DURATION_MS = 3500;

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const shouldRequestDisplayName = (user, options = {}) =>
  Boolean(options?.isNewUser) || !String(user?.name || "").trim();

const getSafeLoginReturnTo = () => {
  if (typeof window === "undefined") return "";

  const returnTo = window.sessionStorage.getItem(LOGIN_RETURN_TO_KEY);
  if (
    typeof returnTo === "string" &&
    returnTo.startsWith("/") &&
    !returnTo.startsWith("//")
  ) {
    return returnTo;
  }

  return "";
};

const UserLoginModal = ({
  isLoginModalOpen,
  setIsLoginModalOpen,
}) => {
  const {
    dispatch,
    handleLoginSession,
    pendingDisplayNameUser,
    setPendingDisplayNameUser,
  } = useAuthContext();
  const queryClient = useQueryClient();
  const [formLoader, setFormLoader] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [hasAgreed, setHasAgreed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [inlineNotice, setInlineNotice] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [onboardingUser, setOnboardingUser] = useState(null);
  const initialScrollYRef = useRef(0);

  const restoreDocumentScroll = (delay = 0) => {
    if (typeof window === "undefined") return;

    const restore = () => {
      window.scrollTo(0, initialScrollYRef.current || 0);
    };

    if (delay > 0) {
      window.setTimeout(restore, delay);
      return;
    }

    window.requestAnimationFrame(restore);
  };

  const showInlineNotice = (message, variant = "success") => {
    setInlineNotice({
      id: Date.now(),
      message,
      variant,
    });
  };

  const resetForm = () => {
    setEmail("");
    setCode("");
    setStep("email");
    setHasAgreed(false);
    setResendCooldown(0);
    setFormLoader(false);
    setInlineNotice(null);
    setDisplayName("");
    setOnboardingUser(null);
  };

  const closeModal = () => {
    if (typeof document !== "undefined") {
      document.activeElement?.blur?.();
    }
    restoreDocumentScroll();
    restoreDocumentScroll(120);
    restoreDocumentScroll(320);
    resetForm();
    setIsLoginModalOpen(false);
  };

  const redirectToStoredReturnPath = () => {
    const returnTo = getSafeLoginReturnTo();
    if (!returnTo) return false;

    window.sessionStorage.removeItem(LOGIN_RETURN_TO_KEY);
    window.location.assign(returnTo);
    return true;
  };

  const finishLoginFlow = () => {
    if (!redirectToStoredReturnPath()) {
      closeModal();
    }
  };

  const completeLogin = (token, apiUser = {}, options = {}) => {
    const decodedUser = jwtDecode(token);
    const user = {
      ...decodedUser,
      ...apiUser,
      userId: decodedUser?.userId || apiUser?.id,
      id: apiUser?.id || decodedUser?.userId,
      role: decodedUser?.role || apiUser?.role,
      username: decodedUser?.username || apiUser?.username,
      name: decodedUser?.name || apiUser?.name,
      profilePicture:
        decodedUser?.profilePicture || apiUser?.profilePicture,
      exp: decodedUser?.exp,
      iat: decodedUser?.iat,
    };

    Cookies.set("userId", user?.userId, {
      expires: new Date(user.exp * 1000),
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
      path: "/",
    });

    Cookies.set("role", user?.role, {
      expires: new Date(user.exp * 1000),
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
      path: "/",
    });

    localStorage.setItem("jwtToken", token);
    handleLoginSession(user?.userId, "success");
    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });

    queryClient.removeQueries({ queryKey: ["posts"] });
    queryClient.invalidateQueries({
      queryKey: ["posts"],
      refetchType: "active",
    });

    setTimeout(() => {
      queryClient.refetchQueries({ queryKey: ["posts"] });
    }, 100);

    if (shouldRequestDisplayName(user, options)) {
      setOnboardingUser(user);
      setDisplayName(user?.name || "");
      setInlineNotice(null);
      setStep("name");
      setCode("");
      return;
    }

    finishLoginFlow();
  };

  const requestEmailCode = async () => {
    const normalizedEmail = normalizeEmail(email);
    const isResendRequest = step === "code";

    if (!hasAgreed) {
      showInlineNotice("이용약관 및 개인정보처리방침에 동의해 주세요.", "error");
      return;
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      showInlineNotice("유효한 이메일 주소를 입력해 주세요.", "error");
      return;
    }

    try {
      setFormLoader(true);
      await startEmailLogin({ email: normalizedEmail });
      setEmail(normalizedEmail);
      setCode("");
      setStep("code");
      setResendCooldown(RESEND_CODE_COOLDOWN_SECONDS);
      showInlineNotice(
        isResendRequest
          ? "새 인증번호를 보냈어요."
          : "이메일로 인증코드를 보냈습니다.",
        "success"
      );
    } catch (error) {
      showInlineNotice(
        error?.response?.data?.message ||
          error?.message ||
          "인증코드를 보낼 수 없습니다.",
        "error"
      );
    } finally {
      setFormLoader(false);
    }
  };

  const verifyEmailCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      showInlineNotice("6자리 인증코드를 입력해 주세요.", "error");
      return;
    }

    try {
      setFormLoader(true);
      const response = await verifyEmailLogin({
        email: normalizeEmail(email),
        code,
      });

      if (!response?.token) {
        showInlineNotice("로그인 응답이 올바르지 않습니다.", "error");
        return;
      }

      completeLogin(response.token, response.user || {}, {
        isNewUser: Boolean(response?.isNewUser),
      });
    } catch (error) {
      showInlineNotice(
        error?.response?.data?.message ||
          error?.message ||
          "인증코드를 확인하지 못했습니다.",
        "error"
      );
    } finally {
      setFormLoader(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (step === "name") {
      saveDisplayName();
      return;
    }
    if (step === "code") {
      verifyEmailCode();
      return;
    }
    requestEmailCode();
  };

  const handleCodeChange = (event) => {
    const nextCode = event.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(nextCode);
  };

  const handleResendCode = () => {
    if (formLoader) return;
    if (resendCooldown > 0) {
      showInlineNotice("인증코드는 잠시 후 다시 요청할 수 있습니다.", "info");
      return;
    }
    requestEmailCode();
  };

  const saveDisplayName = async () => {
    const nextName = displayName.trim();

    if (!nextName) {
      showInlineNotice("계정 이름을 입력해 주세요.", "error");
      return;
    }

    if (nextName.length > 30) {
      showInlineNotice("계정 이름은 30자 이하로 입력해 주세요.", "error");
      return;
    }

    if (!onboardingUser?.userId && !onboardingUser?.id) {
      showInlineNotice("사용자 정보를 확인하지 못했습니다.", "error");
      return;
    }

    try {
      setFormLoader(true);
      const updatedUser = await updateUserProfileInfo(
        onboardingUser?.userId || onboardingUser?.id,
        { name: nextName }
      );
      dispatch({
        type: "LOGIN",
        payload: {
          user: {
            ...onboardingUser,
            ...(updatedUser || {}),
            userId:
              onboardingUser?.userId ||
              updatedUser?.userId ||
              updatedUser?.id,
            id: updatedUser?.id || onboardingUser?.id,
            name: updatedUser?.name || nextName,
          },
        },
      });
      setPendingDisplayNameUser?.(null);
      finishLoginFlow();
    } catch (error) {
      showInlineNotice(
        error?.response?.data?.message ||
          error?.message ||
          "계정 이름을 저장하지 못했습니다.",
        "error"
      );
    } finally {
      setFormLoader(false);
    }
  };

  const requireAgreementBeforeGoogle = () => {
    if (hasAgreed) return true;
    showInlineNotice("이용약관 및 개인정보처리방침에 동의해 주세요.", "error");
    return false;
  };

  useEffect(() => {
    if (step !== "code" || resendCooldown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendCooldown((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [step, resendCooldown]);

  useEffect(() => {
    if (!inlineNotice) return undefined;

    const timer = window.setTimeout(() => {
      setInlineNotice(null);
    }, AUTH_NOTICE_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [inlineNotice]);

  useEffect(() => {
    if (!isLoginModalOpen || !pendingDisplayNameUser?.userId) return;

    setOnboardingUser(pendingDisplayNameUser);
    setDisplayName(pendingDisplayNameUser?.name || "");
    setHasAgreed(true);
    setInlineNotice(null);
    setStep("name");
  }, [isLoginModalOpen, pendingDisplayNameUser]);

  useEffect(() => {
    if (!isLoginModalOpen || typeof window === "undefined") return undefined;

    const root = document.documentElement;
    const body = document.body;
    initialScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;

    const viewportHeight = window.innerHeight || 0;
    const safeHeight = Math.max(320, Math.floor(viewportHeight));
    root.style.setProperty("--hotple-auth-vh", `${safeHeight}px`);
    root.style.setProperty("--hotple-auth-top", "0px");
    root.style.setProperty("--hotple-auth-card-max-h", `${safeHeight}px`);
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      root.style.removeProperty("--hotple-auth-vh");
      root.style.removeProperty("--hotple-auth-top");
      root.style.removeProperty("--hotple-auth-offset-y");
      root.style.removeProperty("--hotple-auth-card-max-h");
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyOverflow;
      restoreDocumentScroll();
      restoreDocumentScroll(120);
      restoreDocumentScroll(320);
    };
  }, [isLoginModalOpen]);

  const noticeStyles = {
    success:
      "border-[#bfe8c9] bg-[#f0fbf3] text-[#17803a]",
    info: "border-[#b8d7ff] bg-[#eef6ff] text-[#1d7cff]",
    error: "border-[#ffc8d0] bg-[#fff1f3] text-[#d92d4f]",
  };

  return (
    <Modal
      isOpen={isLoginModalOpen}
      onClose={closeModal}
      showTitle={false}
      overlayClassName="hotple-auth-overlay !items-start !justify-center !overflow-hidden md:!items-center"
      overlayStyle={{
        minHeight: "100vh",
      }}
      modalStyle={{
        maxHeight: "var(--hotple-auth-card-max-h, calc(100dvh - 32px))",
      }}
      className="hotple-auth-modal bg-white dark:!bg-white shadow-none md:!w-[480px] md:!max-w-[480px] md:overscroll-contain md:!rounded-[28px] md:!shadow-[0_22px_60px_rgba(0,0,0,0.16)] overflow-y-auto overflow-x-hidden"
      contentClass="!block !p-0"
    >
      <div className="hotple-auth-content relative w-full px-7 py-8 text-center sm:px-11 sm:py-12">
        <button
          type="button"
          aria-label="닫기"
          className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center rounded-full text-[26px] font-light leading-none text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-700"
          onClick={closeModal}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        {step === "code" && (
          <button
            type="button"
            aria-label="뒤로가기"
            className="absolute left-6 top-6 flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900"
            onClick={() => {
              setCode("");
              setResendCooldown(0);
              setStep("email");
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        )}

        <div className="hotple-auth-logo-wrap mx-auto h-24 w-24">
          <img
            src="/icons/auth/2.4.svg"
            alt="Hotple"
            className="hotple-auth-logo h-24 w-24 object-contain"
            style={{ transform: "translateY(14px)" }}
          />
        </div>

        {step === "code" ? (
          <>
            <h2 className="hotple-auth-title mt-5 text-[25px] font-extrabold leading-tight tracking-normal text-neutral-950">
              본인이 맞는지 확인해 주세요
            </h2>
            <p className="hotple-auth-subtitle mt-3 text-[14px] font-semibold leading-5 text-neutral-400">
              코드를 {email} 주소로 보내드렸습니다.
            </p>
          </>
        ) : step === "name" ? (
          <>
            <h2 className="hotple-auth-title mt-5 text-[25px] font-extrabold leading-tight tracking-normal text-neutral-950">
              프로필 이름을 정해주세요
            </h2>
            <p className="hotple-auth-subtitle mt-3 text-[14px] font-semibold leading-5 text-neutral-400">
              피드, 댓글, 프로필에 표시되는 이름이에요.
            </p>
          </>
        ) : (
          <>
            <h2 className="hotple-auth-title mt-5 text-[25px] font-extrabold leading-tight tracking-normal text-neutral-950">
              로그인 또는 회원가입
            </h2>
            <p className="hotple-auth-subtitle mt-3 text-[14px] font-semibold leading-5 text-neutral-400">
              이메일만 있으면 간편하게 시작할 수 있어요.
            </p>
          </>
        )}

        <form className="hotple-auth-form mt-7 flex flex-col gap-4" onSubmit={handleSubmit}>
          {step === "code" ? (
            <input
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6자리 코드"
              className="h-[56px] rounded-[10px] border border-neutral-200 bg-white px-4 text-center text-[24px] font-extrabold tracking-[0.35em] text-neutral-900 outline-none transition placeholder:text-[15px] placeholder:font-semibold placeholder:tracking-normal placeholder:text-neutral-400 focus:border-neutral-300"
              value={code}
              onChange={handleCodeChange}
            />
          ) : step === "name" ? (
            <label className="flex h-[52px] items-center gap-3 rounded-[10px] border border-neutral-200 bg-white px-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition focus-within:border-neutral-300">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-[22px] w-[22px] shrink-0 text-neutral-400"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.8"
              >
                <path d="M20 21a8 8 0 1 0-16 0" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <input
                name="displayName"
                type="text"
                autoComplete="name"
                placeholder="계정 이름"
                className="h-full min-w-0 flex-1 bg-transparent text-[15px] font-semibold text-neutral-800 outline-none placeholder:text-neutral-400"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoFocus
              />
            </label>
          ) : (
            <label className="flex h-[52px] items-center gap-3 rounded-[10px] border border-neutral-200 bg-white px-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition focus-within:border-neutral-300">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-[22px] w-[22px] shrink-0 text-neutral-400"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.8"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
              <input
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="이메일 주소"
                className="h-full min-w-0 flex-1 bg-transparent text-[15px] font-semibold text-neutral-800 outline-none placeholder:text-neutral-400"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
          )}

          <button
            type="submit"
            disabled={formLoader}
            className="mt-1 flex h-[54px] w-full items-center justify-center rounded-[10px] bg-gradient-to-r from-[#f31f59] to-[#d90785] text-[15px] font-extrabold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {formLoader
              ? "처리 중..."
              : step === "code"
                ? "확인"
                : step === "name"
                  ? "시작하기"
                  : "계속"}
          </button>
        </form>

        {inlineNotice && (
          <div
            key={inlineNotice.id}
            role="status"
            aria-live="polite"
            className={`mt-4 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[8px] border px-4 text-[13px] font-extrabold ${noticeStyles[inlineNotice.variant] || noticeStyles.success}`}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-[17px] w-[17px]"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.4"
              >
                {inlineNotice.variant === "error" ? (
                  <>
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </>
                ) : (
                  <>
                    <circle cx="12" cy="12" r="8" />
                    <path d="m8.7 12.2 2.1 2.1 4.5-4.8" />
                  </>
                )}
              </svg>
            </span>
            <span>{inlineNotice.message}</span>
          </div>
        )}

        {step === "code" ? (
          <div className="hotple-auth-extra mt-6 space-y-3 text-[13px] font-semibold">
            <div className="text-neutral-500">
              <span>메일이 오지 않았나요? </span>
              <button
                type="button"
                className={`font-extrabold text-[#1d7cff] ${
                  resendCooldown > 0 ? "cursor-default" : "cursor-pointer"
                } disabled:cursor-not-allowed`}
                disabled={formLoader}
                onClick={handleResendCode}
              >
                {resendCooldown > 0
                  ? `${resendCooldown}초 후 다시 보내기`
                  : "다시 보내기"}
              </button>
            </div>
            <p className="text-neutral-400">
              이 이메일을 사용할 수 없나요?
              <Link
                href="/account-recovery"
                className="ml-2 font-extrabold text-[#f31f59] hover:underline"
                onClick={() => setIsLoginModalOpen(false)}
              >
                계정 찾기
              </Link>
            </p>
          </div>
        ) : step === "name" ? (
          <p className="hotple-auth-extra mt-6 text-[13px] font-semibold leading-5 text-neutral-400">
            이 이름은 나중에 변경할 수 있어요.
          </p>
        ) : (
          <>
            <div className="hotple-auth-extra my-7 grid grid-cols-[1fr_auto_1fr] items-center gap-5">
              <span className="h-px bg-neutral-200" />
              <span className="text-[13px] font-bold text-neutral-500">또는</span>
              <span className="h-px bg-neutral-200" />
            </div>

            <div className="hotple-auth-extra">
              <GoogleSignIn
                onClose={closeModal}
                btnText="Google로 계속하기"
                onBeforeStart={requireAgreementBeforeGoogle}
                btnClass={`!h-[46px] !rounded-[10px] !border !border-neutral-200 !bg-white !p-0 !text-[14px] !font-extrabold !text-neutral-800 !shadow-none hover:!bg-neutral-50 hover:!shadow-none ${
                  !hasAgreed ? "!opacity-60" : ""
                }`}
                iconClass="!h-5 !w-5 !mr-0"
                textClass="!ml-5"
              />
            </div>

            <label className="hotple-auth-agreement mt-8 flex cursor-pointer items-start gap-2.5 text-left text-[12px] font-semibold leading-5 text-neutral-500">
              <input
                type="checkbox"
                className="mt-[2px] h-4 w-4 rounded border-neutral-300 accent-[#f31f59]"
                checked={hasAgreed}
                onChange={(event) => setHasAgreed(event.target.checked)}
              />
              <span className="flex-1">
                계속하면 이용약관 및 개인정보처리방침에 동의하게 됩니다.
              </span>
            </label>

            <p className="hotple-auth-extra mt-7 text-[13px] font-semibold text-neutral-400">
              이전 이메일을 사용할 수 없나요?
              <Link
                href="/account-recovery"
                className="ml-3 font-extrabold text-[#f31f59] hover:underline"
                onClick={() => setIsLoginModalOpen(false)}
              >
                계정 찾기
              </Link>
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};

export default UserLoginModal;
